import { OrderStatus, TimeSlot } from "../features/orders/types";

export const formatMoney = (value: number | string | null | undefined) =>
  `${Number(value || 0).toFixed(2)} QAR`;

// The API returns some timestamps as ISO strings and older ones as epoch ms
export const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = /^\d+$/.test(value) ? new Date(Number(value)) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value?: string | null) => {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** "YYYY-MM-DD" in the device's local time */
export const toDateKey = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const nextDays = (count: number): string[] =>
  Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return toDateKey(d);
  });

export const formatDayLabel = (dateKey: string) => {
  if (dateKey === toDateKey(new Date())) return "Today";
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateKey === toDateKey(tomorrow)) return "Tomorrow";
  return new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatTime = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hour}:${String(m).padStart(2, "0")} ${suffix}` : `${hour} ${suffix}`;
};

export const formatSlotTime = (slot: Pick<TimeSlot, "start_time" | "end_time">) =>
  `${formatTime(slot.start_time)} – ${formatTime(slot.end_time)}`;

export const formatSlot = (slot?: TimeSlot | null, date?: string | null) =>
  slot && date ? `${formatDayLabel(date)}, ${formatSlotTime(slot)}` : "";

export const STATUS_STEPS: OrderStatus[] = [
  "open",
  "ready_to_pickup",
  "collected",
  "received",
  "processing",
  "ready_for_delivery",
  "delivered",
];

export const STATUS_LABELS: Record<OrderStatus, string> = {
  open: "Order placed",
  ready_to_pickup: "Courier on the way",
  collected: "Bag collected",
  received: "At the shop",
  processing: "Cleaning",
  ready_for_delivery: "Ready for delivery",
  delivered: "Delivered",
};
